import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { catalogCnaes } from '@/shared/api/resources';
import { formatCnae } from '@/shared/lib/format';

import { Button } from './Button';
import { Field } from './Field';

/** O catalogo muda por migration, e nao durante o uso. */
const UMA_HORA = 60 * 60 * 1000;

export interface CnaePickerProps {
  label: string;
  /** Codigo gravado: sete digitos, ou o texto do cadastro antigo. */
  value: string;
  /** Descricao a mostrar ao lado do codigo escolhido. */
  description?: string | undefined;
  onChange: (code: string, description: string) => void;
  hint?: string | undefined;
  error?: string | undefined;
  disabled?: boolean | undefined;
  /** Texto do botao que tira o CNAE escolhido. Sem ele, o botao nao aparece. */
  clearLabel?: string | undefined;
}

/**
 * Escolha de um CNAE da CNAE 2.3 do IBGE (P-TOM-C1, TOM-043).
 *
 * O catalogo tem 1.332 subclasses e ninguem escolhe numa lista desse tamanho: a
 * busca e por codigo ou descricao, como a do catalogo da plataforma
 * (`GET /api/v1/catalogs/cnae-codes`), e o resultado e uma lista de botoes. O codigo
 * vai com os sete digitos, e o servidor confere de novo. O CNAE antigo, gravado em
 * texto livre, aparece como foi gravado, e so muda se a pessoa escolher outro.
 */
export function CnaePicker({
  label,
  value,
  description,
  onChange,
  hint,
  error,
  disabled,
  clearLabel,
}: CnaePickerProps) {
  const [busca, setBusca] = useState('');
  const termo = busca.trim();
  const resultados = useQuery({
    queryKey: ['catalogs', 'cnae', termo],
    queryFn: ({ signal }) => catalogCnaes.search(termo, signal),
    enabled: termo.length >= 2 && !disabled,
    staleTime: UMA_HORA,
  });

  const doCatalogo = /^\d{7}$/.test(value.trim());
  const escolhido = value.trim()
    ? doCatalogo
      ? `${formatCnae(value)}${description ? ` - ${description}` : ''}`
      : `${value} (como gravado)`
    : '';

  return (
    <div className="flex flex-col gap-2">
      <Field
        label={label}
        value={busca}
        placeholder="Digite o codigo ou a descricao"
        hint={hint ?? 'Busca na CNAE 2.3 do IBGE, por codigo ou descricao.'}
        error={error}
        disabled={disabled}
        onChange={(event) => setBusca(event.target.value)}
      />
      {escolhido ? (
        <div className="flex flex-wrap items-center gap-2 text-sm text-ink-800">
          <span>
            Escolhido: <strong>{escolhido}</strong>
          </span>
          {clearLabel && !disabled ? (
            <Button size="sm" variant="ghost" onClick={() => onChange('', '')}>
              {clearLabel}
            </Button>
          ) : null}
        </div>
      ) : null}
      {termo.length >= 2 && !disabled ? (
        <div className="rounded-md border border-line p-2">
          {resultados.isPending ? (
            <p className="text-2xs text-ink-500">Buscando na CNAE.</p>
          ) : resultados.isError ? (
            <p className="text-2xs text-danger-600">
              A busca na CNAE nao respondeu. Tente de novo.
            </p>
          ) : (resultados.data ?? []).length === 0 ? (
            <p className="text-2xs text-ink-500">Nenhum CNAE encontrado.</p>
          ) : (
            <ul className="flex max-h-56 flex-col gap-1 overflow-y-auto">
              {(resultados.data ?? []).map((cnae) => (
                <li key={cnae.code}>
                  <button
                    type="button"
                    className="w-full rounded px-2 py-1 text-left text-sm text-ink-800 hover:bg-surface-sunken focus:outline-none focus:ring-3 focus:ring-brand-500/15"
                    onClick={() => {
                      onChange(cnae.code, cnae.description);
                      setBusca('');
                    }}
                  >
                    {`${cnae.formatted_code} - ${cnae.description}`}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}
